<script setup lang="ts">
import {
    getShowPostalAddressFields,
    isNaturalProfileAttributeLocked,
    usePersonalAccountDataState,
} from '../../../../utilities/account/useAccountDataPersonal'
import BaseInputText from '../../../base/BaseInputText.vue'
import BaseInputCombobox from '../../../base/BaseInputCombobox.vue'
import { getSelectableCountryCodes } from '../../../../data'
import { GlobeIcon, LocationMarkerIcon } from '@heroicons/vue/solid'
import {
    isNaturalProfileAttributeHidden,
    isRequiredNaturalProfileAttributesForInvestment,
    isRequiredRequiredNaturalProfileAttributes,
} from '../../../../validation'
import { initPersonalAccountForm } from '../../../../utilities/account/useAccountFormPersonal'
import { ref, watch } from 'vue'
import { HomeIcon } from '@heroicons/vue/outline'
import BaseInputCheckbox from '../../../base/BaseInputCheckbox.vue'
import BaseHeader from '../../../base/BaseHeader.vue'

const { naturalProfile: account } = usePersonalAccountDataState()

initPersonalAccountForm('Address Data')
const postalAddressEnabled = getShowPostalAddressFields()
const isPostalAddressSameAsPhysicalAddress = ref(false)

watch([account, isPostalAddressSameAsPhysicalAddress], () => {
    if (isPostalAddressSameAsPhysicalAddress.value) {
        account.postal_address = account.address
        account.postal_address_postal_code = account.postal_code
        account.postal_address_city = account.city
        account.postal_address_state = account.state
        account.postal_address_country = account.country
    }
})
</script>

<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:grid-cols-2 sm:px-6">
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.address"
                :required="isRequiredRequiredNaturalProfileAttributes('address')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('address')"
                :disabled="isNaturalProfileAttributeLocked('address')"
                :icon="LocationMarkerIcon"
                name="account.address"
                :label="$t('account.address')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.postal_code"
                :icon="LocationMarkerIcon"
                :required="isRequiredRequiredNaturalProfileAttributes('postal_code')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('postal_code')"
                :disabled="isNaturalProfileAttributeLocked('postal_code')"
                name="account.postal_code"
                :label="$t('account.postal_code')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.city"
                :icon="LocationMarkerIcon"
                :required="isRequiredRequiredNaturalProfileAttributes('city')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('city')"
                :disabled="isNaturalProfileAttributeLocked('city')"
                name="account.city"
                :label="$t('account.city')" />
        </div>
        <div v-if="!isNaturalProfileAttributeHidden('state')" class="sm:col-span-1">
            <BaseInputText
                v-model="account.state"
                :icon="LocationMarkerIcon"
                :required="isRequiredRequiredNaturalProfileAttributes('state')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('state')"
                :disabled="isNaturalProfileAttributeLocked('state')"
                name="account.state"
                :label="$t('account.state')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCombobox
                v-model="account.country"
                :icon="GlobeIcon"
                :required="isRequiredRequiredNaturalProfileAttributes('country')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('country')"
                :disabled="isNaturalProfileAttributeLocked('country')"
                name="account.country"
                :options="getSelectableCountryCodes()"
                nullable
                :label="$t('account.country')" />
        </div>
    </div>

    <div v-if="postalAddressEnabled">
        <BaseHeader
            class="border-t border-gray-200"
            :title="$t('account_settings.postal_address_data')"
            :icon="HomeIcon">
            <BaseInputCheckbox
                v-model="isPostalAddressSameAsPhysicalAddress"
                standalone
                class="mt-2"
                name="isPostalAddressSameAsPhysicalAddress"
                :label="$t('account.same_as_residential')" />
        </BaseHeader>
        <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:grid-cols-2 sm:px-6">
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="account.postal_address"
                    :required="isRequiredRequiredNaturalProfileAttributes('address')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('address')"
                    :disabled="isNaturalProfileAttributeLocked('address') || isPostalAddressSameAsPhysicalAddress"
                    :icon="LocationMarkerIcon"
                    name="account.postal_address"
                    :label="$t('account.address')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="account.postal_address_postal_code"
                    :icon="LocationMarkerIcon"
                    :required="isRequiredRequiredNaturalProfileAttributes('postal_code')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('postal_code')"
                    :disabled="isNaturalProfileAttributeLocked('postal_code') || isPostalAddressSameAsPhysicalAddress"
                    name="account.postal_address_postal_code"
                    :label="$t('account.postal_code')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="account.postal_address_city"
                    :icon="LocationMarkerIcon"
                    :required="isRequiredRequiredNaturalProfileAttributes('city')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('city')"
                    :disabled="isNaturalProfileAttributeLocked('city') || isPostalAddressSameAsPhysicalAddress"
                    name="account.postal_address_city"
                    :label="$t('account.city')" />
            </div>
            <div v-if="!isNaturalProfileAttributeHidden('state')" class="sm:col-span-1">
                <BaseInputText
                    v-model="account.postal_address_state"
                    :icon="LocationMarkerIcon"
                    :required="isRequiredRequiredNaturalProfileAttributes('state')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('state')"
                    :disabled="isNaturalProfileAttributeLocked('state') || isPostalAddressSameAsPhysicalAddress"
                    name="account.postal_address_state"
                    :label="$t('account.state')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputCombobox
                    v-model="account.postal_address_country"
                    :icon="GlobeIcon"
                    :required="isRequiredRequiredNaturalProfileAttributes('country')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('country')"
                    :disabled="isNaturalProfileAttributeLocked('country') || isPostalAddressSameAsPhysicalAddress"
                    name="account.postal_address_country"
                    :options="getSelectableCountryCodes()"
                    nullable
                    :label="$t('account.country')" />
            </div>
        </div>
    </div>
</template>
